import React, { useState } from "react";
import styles from "./Calendar.module.scss";
import Icon from '../images/Icon';

interface CalendarProps {
  initialYear?: number;
  initialMonth?: number;
  onDateClick: (date: string) => void; 
  showHourPanel?: boolean; 
  onClose?: () => void;
}

export const Calendar: React.FC<CalendarProps> = ({ initialYear, initialMonth, onDateClick, showHourPanel = false , onClose}) => {
  const [year, setYear] = useState<number>(initialYear || new Date().getFullYear());
  const [month, setMonth] = useState<number>(initialMonth || new Date().getMonth());
  const [view, setView] = useState<"month" | "year">("month");
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedHour] = useState<number | null>(null);
  const [selectedMinute] = useState<number | null>(null);

  const daysInMonth = (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getWeekDay = (year: number, month: number, day: number): number => {
    return new Date(year, month, day).getDay();
  };

  const generateMonthView = () => {
    const totalDays = daysInMonth(year, month);
    const firstDayIndex = getWeekDay(year, month, 1);
    const weeks: (number | null)[][] = [];
    let currentWeek: (number | null)[] = Array((firstDayIndex === 0 ? 6 : firstDayIndex - 1)).fill(null);

    for (let day = 1; day <= totalDays; day++) {
      currentWeek.push(day);
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }

    return weeks;
  };

  const handleMonthChange = (direction: "prev" | "next") => {
    if (direction === "prev") {
      if (month === 0) {
        setMonth(11);
        setYear(year - 1);
      } else {
        setMonth(month - 1);
      }
    } else {
      if (month === 11) {
        setMonth(0);
        setYear(year + 1);
      } else {
        setMonth(month + 1);
      }
    }
  };

  const handleYearChange = (selectedYear: number) => {
    setYear(selectedYear);
    setView("month");
  };

  const renderYearView = () => {
    const years = Array.from({ length: 25 }, (_, i) => year - 12 + i);
    return (
      <div className={styles.yearGrid}>
        {years.map((y) => (
          <div
            key={y}
            onClick={() => handleYearChange(y)}
            className={`${styles.yearBox} ${y === year ? styles.selectedYear : ""}`} 
          >
            {y}
          </div>
        ))}
      </div>
    );
  };

  const renderMonthView = () => {
    const weeks = generateMonthView();
    return (
      <div className={styles.monthGrid}>
        {weeks.flat().map((day, index) => (
<div
  key={index}
  className={`${styles.day} ${day === null ? styles.empty : ""} ${
    day === new Date().getDate() &&
    month === new Date().getMonth() &&
    year === new Date().getFullYear()
      ? styles.today
      : ""
  } ${day === selectedDay ? styles.selectedDay : ""}`} // Ajout de styles.selectedDay
  onClick={() => handleDayClick(day)}
>
  {day || ""}
</div>
        ))}
      </div>
    );
  };

  const renderDayView = () => {
    return (
      <div className={styles.timeSlotsContainer}>
        {Array.from({ length: 24 }, (_, hour) => (
          <React.Fragment key={hour}>
            {Array.from({ length: 4 }, (_, minuteIndex) => {
              const minute = minuteIndex * 15;
              return (
                <div
  key={`${hour}:${minute}`}
  className={`${styles.timeSlot} ${
    hour === selectedHour && minute === selectedMinute ? styles.selected : ""
  }`}
  onClick={() => handleTimeSlotClick(hour, minute)}
>
  {`${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`}
</div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    );
  };

/*
  const handleDayClick = (day: number | null) => {
    if (day !== null) {
      setSelectedDay(day);

      if ( !showHourPanel && selectedDay !== null )
       {
        const date = `${selectedDay.toString().padStart(2, "0")}/${(month + 1).toString().padStart(2, "0")}/${year}`;
        onDateClick(date);
      }
    }
  };
  */
  const handleDayClick = (day: number | null) => {
  if (day !== null) {
    setSelectedDay(day);
    if (!showHourPanel) {
      const date = `${day.toString().padStart(2, "0")}/${(month + 1).toString().padStart(2, "0")}/${year}`;
      onDateClick(date);
    }
  }
};


  const handleTimeSlotClick = (hour: number, minute: number) => {
    if (selectedDay !== null) {
    //  const date = `${year}/${(month + 1).toString().padStart(2, "0")}/${selectedDay.toString().padStart(2, "0")} ${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      const date = `${selectedDay.toString().padStart(2, "0")}/${(month + 1).toString().padStart(2, "0")}/${year} ${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      onDateClick(date);
    }
  };

  return (
    <div className={styles.calendar}>

      {/* Bouton de fermeture */}
      {onClose && (
        <div className={styles.window_close_button}>
          <Icon
            name="cross"
            onClick={onClose}
            color="#000000"
            hoverColor="#FFFF00"
			width={10}
			height={10}
          />
        </div>
      )}
	  
      <div className={styles.mainContent}>
        <div className={styles.header}>
          {view === "month" ? (
            <>
              <button onClick={() => handleMonthChange("prev")}>&lt;</button>
              <span className={styles.title} onClick={() => setView("year")}>
                {new Date(year, month).toLocaleString("default", { month: "long" })} {year}
              </span>
              <button onClick={() => handleMonthChange("next")}>&gt;</button>
            </>
          ) : (
            <>
              <button onClick={() => setYear(year - 25)}>&lt;</button>
              <span className={styles.title}>{year - 12} - {year + 12}</span>
              <button onClick={() => setYear(year + 25)}>&gt;</button>
            </>
          )}
        </div>

        {view === "month" ? (
          <>
            <div className={styles.weekDays}>
              {["L", "M", "M", "J", "V", "S", "D"].map((day, index) => (
                <div key={index} className={styles.weekDay}>
                  {day}
                </div>
              ))}
            </div>
            {renderMonthView()}
          </>
        ) : (
          renderYearView()
        )}
      </div>

      {showHourPanel && (
        <div className={styles.rightPanel}>{renderDayView()}</div>
      )}
    </div>
  );
};

export default Calendar;
